import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import HindiNotifyPanel, { useHindiNotify } from './HindiNotifyPanel';
import PaceAnalytics from './PaceAnalytics';
import QuantDashboard from './QuantDashboard';
import OptionScope from './OptionScope';
import { useSessionKeepAlive, SessionBanner } from './SessionKeepAlive';
import ServerErrorBanner, { useServerErrors } from './ServerErrorBanner';
import { formatIstDateKey, formatActivityWhen } from './ist-datetime';
import './OptionsTrading.css';

const STATUS_LABELS = {
  running: 'Trading live',
  waiting: 'Waiting for market',
  target_hit: 'Target hit — stopped',
  max_loss_hit: 'Max loss — stopped',
  loss_protection_stop: 'Loss protection — stopped',
  market_closed: 'Market closed',
  weekend: 'Weekend — closed',
  disabled: 'Disabled',
  idle: 'Starting…'
};

const STATUS_PILL = {
  running: 'live',
  waiting: 'closed',
  target_hit: 'gold',
  max_loss_hit: 'stop',
  loss_protection_stop: 'stop',
  market_closed: 'closed',
  weekend: 'closed',
  disabled: 'closed',
  idle: 'closed'
};

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const OptionsTrading = () => {
  const [prices, setPrices] = useState([]);
  const [portfolio, setPortfolio] = useState({});
  const [trades, setTrades] = useState([]);
  const [autoStatus, setAutoStatus] = useState(null);
  const [settings, setSettings] = useState({
    symbol: '^NSEI',
    underlyingName: 'NIFTY',
    optionType: 'auto',
    lots: 1,
    lotSize: 50,
    dailyTarget: 100,
    maxDailyInvestment: 100,
    maxDailyLoss: 0,
    stopLossPerTrade: 18,
    relentlessTarget: true,
    triggerPct: 0.04,
    pollSeconds: 15,
    autoStartAtMarketOpen: true,
    autoOpenBrowser: false,
    squareOffAtClose: true,
    handsFree: true,
    enabled: true
  });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyHistory, setDailyHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('today');
  const [stressMeta, setStressMeta] = useState(null);
  const [stressPage, setStressPage] = useState(null);
  const [stressReady, setStressReady] = useState(null);
  const [stressBusy, setStressBusy] = useState(false);
  const [stressPageNo, setStressPageNo] = useState(1);
  const [stressToast, setStressToast] = useState('');
  const { errors: serverErrors, pushError, clearSource, dismiss: dismissServerError, clearAll: clearAllServerErrors } = useServerErrors();

  const symbols = ['^NSEI', 'RELIANCE.NS', 'TCS.NS', 'HDFCBANK.NS', 'INFY.NS'];

  const refresh = useCallback(async () => {
    try {
      const [priceRes, portRes, logRes, statusRes, todayRes, historyRes] = await Promise.all([
        axios.get(`/api/quote?symbols=${symbols.join(',')}`),
        axios.get('/api/portfolio'),
        axios.get('/api/logs'),
        axios.get('/api/automation/status'),
        axios.get('/api/daily/today'),
        axios.get('/api/daily/history?limit=30')
      ]);
      setPrices(priceRes.data);
      setPortfolio(portRes.data);
      setTrades(logRes.data.trades || []);
      setAutoStatus(statusRes.data);
      setDailyReport(todayRes.data);
      setDailyHistory(historyRes.data);
      clearSource('refresh');
    } catch (e) {
      pushError('refresh', e);
    }
  }, [pushError, clearSource]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get('/api/automation/settings');
      setSettings(res.data);
      await axios.post('/api/automation/enable');
      clearSource('settings');
    } catch (e) {
      pushError('settings', e);
    }
  }, [pushError, clearSource]);

  useEffect(() => {
    loadSettings();
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [loadSettings, refresh]);

  const loadStressPage = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`/api/options/dummy-stress?page=${page}&pageSize=50`, { timeout: 60000 });
      setStressPage(res.data);
      setStressPageNo(res.data?.page || page);
      if (res.data?.summary) setStressMeta((m) => ({ ...(m || {}), ...res.data.summary }));
    } catch (e) {
      pushError('stress-page', e);
    }
  }, [pushError]);

  const runStressGenerate = useCallback(async (force = false) => {
    setStressBusy(true);
    setStressToast(force ? 'Regenerating 1,00,000 records…' : 'Generating 1,00,000 dummy Options records…');
    try {
      const res = await axios.post('/api/options/dummy-stress/generate', { force, count: 100000 }, { timeout: 180000 });
      setStressMeta(res.data);
      setStressToast(res.data?.generated
        ? `Generated ${res.data.count?.toLocaleString('en-IN')} records · ${res.data.sizeMb} MB · ${res.data.elapsedMs} ms`
        : `Already ready · ${res.data.count?.toLocaleString('en-IN')} records`);
      await loadStressPage(1);
      clearSource('stress-gen');
    } catch (e) {
      pushError('stress-gen', e);
      setStressToast(e.response?.data?.error || e.message || 'Generate failed');
    } finally {
      setStressBusy(false);
    }
  }, [loadStressPage, pushError, clearSource]);

  const runReadiness = useCallback(async () => {
    setStressBusy(true);
    setStressToast('Running tomorrow readiness test…');
    try {
      const res = await axios.get('/api/options/dummy-stress/readiness', { timeout: 120000 });
      setStressReady(res.data);
      setStressToast(res.data?.message || (res.data?.ok ? 'READY' : 'NOT READY'));
      if (res.data?.meta) setStressMeta(res.data.meta);
      clearSource('stress-ready');
    } catch (e) {
      pushError('stress-ready', e);
      setStressToast(e.response?.data?.error || e.message || 'Readiness failed');
    } finally {
      setStressBusy(false);
    }
  }, [pushError, clearSource]);

  useEffect(() => {
    if (activeTab !== 'stress') return;
    (async () => {
      try {
        const meta = await axios.get('/api/options/dummy-stress/meta', { timeout: 30000 });
        setStressMeta(meta.data);
        if (meta.data?.exists && meta.data?.count > 0) await loadStressPage(1);
      } catch { /* first open may 404 until generate */ }
    })();
  }, [activeTab, loadStressPage]);

  const updateSetting = (key, value) => {
    saveSettings({ ...settings, [key]: value });
  };

  const totalDay = autoStatus?.totalDayPnl ?? 0;
  const target = settings.dailyTarget || 100;
  const progress = autoStatus?.progressPct ?? 0;
  const pos = autoStatus?.currentPosition;
  const today = dailyReport?.today || autoStatus?.dailyJournal || {};
  const summary = dailyHistory?.summary || {};
  const status = autoStatus?.status || 'idle';

  const nowBanner = useMemo(() => {
    if (status === 'target_hit') {
      return { className: 'target-hit', text: `Daily target of ${fmt(target)} reached. Auto again tomorrow 9:15 IST.` };
    }
    if (status === 'market_closed' || status === 'weekend') {
      return {
        className: 'relentless',
        text: 'FULLY AUTO armed — no click needed. Bot starts alone at 9:15 IST, picks CE/PE from India ML, takes profit, squares off at 3:15.'
      };
    }
    if (status === 'loss_protection_stop') {
      return { className: 'stopped', text: 'Loss protection — relentless mode will retry next setup.' };
    }
    if (status === 'max_loss_hit') {
      return { className: 'stopped', text: 'Maximum daily loss reached — bot stopped for today.' };
    }
    if (status === 'running' || status === 'waiting') {
      return {
        className: 'relentless',
        text: autoStatus?.lastAction
          || `FULLY AUTO running — trading until ${fmt(target)} profit (India ML CE/PE).`
      };
    }
    return {
      className: 'relentless',
      text: autoStatus?.lastAction || 'FULLY AUTO initializing…'
    };
  }, [status, target, autoStatus?.lastAction]);

  useHindiNotify(autoStatus?.activityLog);
  useSessionKeepAlive();

  return (
    <div className="opt-app">
      <header className="opt-topbar">
        <div className="opt-topbar-left">
          <h1>Options Trading</h1>
          <p>OptionScope live chain · FULLY AUTO paper NIFTY · India ML CE/PE · target ₹{settings.dailyTarget || 100}/day</p>
        </div>
        <div className="opt-topbar-right">
          <Link to="/ledger/options" className="opt-ledger-link">Ledger</Link>
          <span className="opt-status-pill live" title="Hands-free automation">
            🤖 FULLY AUTO
          </span>
          <span className={`opt-status-pill ${STATUS_PILL[status] || 'closed'}`}>
            {STATUS_LABELS[status] || 'Loading'}
          </span>
        </div>
      </header>

      <SessionBanner />

      <ServerErrorBanner
        errors={serverErrors}
        onDismiss={dismissServerError}
        onClearAll={clearAllServerErrors}
      />

      <OptionScope pollMs={8000} />

      <section className="opt-kpi-row" aria-label="Today's summary">
        <div className="opt-kpi">
          <div className="opt-kpi-label">Today&apos;s P&amp;L</div>
          <div className={`opt-kpi-value ${totalDay > 0 ? 'up' : totalDay < 0 ? 'down' : 'neutral'}`}>
            {fmt(totalDay)}
          </div>
          <div className="opt-kpi-sub">
            Realized {fmt(autoStatus?.dailyPnl ?? 0)} · Unrealized {fmt(autoStatus?.unrealizedPnl ?? 0)}
          </div>
        </div>
        <div className="opt-kpi">
          <div className="opt-kpi-label">Profit target</div>
          <div className={`opt-kpi-value ${totalDay >= target ? 'up' : 'neutral'}`}>
            {Math.min(100, Math.round(progress))}%
          </div>
          <div className="opt-kpi-sub">{fmt(totalDay)} of {fmt(target)}</div>
          <div className="opt-progress">
            <div
              className={`opt-progress-fill ${totalDay >= target ? 'done' : ''}`}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        </div>
        <div className="opt-kpi">
          <div className="opt-kpi-label">Budget left today</div>
          <div className="opt-kpi-value neutral">
            {fmt(autoStatus?.investmentRemaining ?? settings.maxDailyInvestment ?? 50)}
          </div>
          <div className="opt-kpi-sub">
            Cap {fmt(settings.maxDailyInvestment ?? 50)}/day · {portfolio.tradesCount ?? 0} trades
          </div>
        </div>
        {(() => {
          const bias = autoStatus?.indiaMlBias;
          const side = bias?.optionBias || '—';
          const conf = bias?.confidence != null ? `${bias.confidence}%` : '—';
          const sideCls = side === 'CE' ? 'up' : side === 'PE' ? 'down' : 'neutral';
          return (
            <div className="opt-kpi">
              <div className="opt-kpi-label">India ML → CE/PE</div>
              <div className={`opt-kpi-value ${sideCls}`}>{side}</div>
              <div className="opt-kpi-sub">
                Confidence {conf}
                {bias?.niftyChange != null ? ` · Nifty ${bias.niftyChange >= 0 ? '+' : ''}${Number(bias.niftyChange).toFixed(2)}%` : ''}
                {bias?.sensexChange != null ? ` · Sensex ${bias.sensexChange >= 0 ? '+' : ''}${Number(bias.sensexChange).toFixed(2)}%` : ''}
              </div>
              {bias?.reason ? (
                <div className="opt-kpi-sub" style={{ marginTop: 4 }}>{bias.reason}</div>
              ) : null}
            </div>
          );
        })()}
      </section>

      <div className={`opt-now-banner ${nowBanner.className}`}>
        <strong>What&apos;s happening now</strong>
        {nowBanner.text}
      </div>

      <nav className="opt-tabs" aria-label="Options sections">
        <button type="button" className={`opt-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>
          Today
        </button>
        <button type="button" className={`opt-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
        <button type="button" className={`opt-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          History
        </button>
        <button type="button" className={`opt-tab ${activeTab === 'stress' ? 'active' : ''}`} onClick={() => setActiveTab('stress')}>
          Stress 1L
        </button>
      </nav>

      {activeTab === 'today' && (
        <>
          <div className="opt-grid-2">
            <div className="opt-card">
              <h3>Open position</h3>
              {pos ? (
                <>
                  <div className="opt-position-title">
                    {settings.underlyingName} {pos.strike} {pos.optionType}
                  </div>
                  <div className="opt-stat-grid">
                    <div className="opt-stat">
                      <span>Entry premium</span>
                      <strong>{fmt(pos.entryPremium)}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Entry spot</span>
                      <strong>{fmt(pos.entrySpot)}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Units</span>
                      <strong>{pos.units ?? pos.lots}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Unrealized P&amp;L</span>
                      <strong className={(autoStatus?.unrealizedPnl ?? 0) >= 0 ? 'up' : 'down'}>
                        {fmt(autoStatus?.unrealizedPnl ?? 0)}
                      </strong>
                    </div>
                  </div>
                </>
              ) : (
                <div className="opt-position-empty">
                  No open position — watching for {settings.triggerPct}% move from day open
                  {autoStatus?.dayOpenPrice ? ` (open ${fmt(autoStatus.dayOpenPrice)})` : ''}
                </div>
              )}
            </div>

            <div className="opt-card">
              <h3>Live prices</h3>
              <div className="opt-ticker">
                {prices.length > 0 ? prices.map((p) => (
                  <div key={p.symbol} className="opt-ticker-chip">
                    <span className="sym">{p.symbol.replace('.NS', '').replace('^', '')}</span>
                    <span className="px">{fmt(p.price)}</span>
                    <span className={`chg ${p.change >= 0 ? 'up' : 'down'}`}>
                      {p.change >= 0 ? '+' : ''}{p.change.toFixed(2)}%
                    </span>
                  </div>
                )) : (
                  <span className="opt-kpi-sub">Fetching quotes…</span>
                )}
              </div>
              <div className="opt-stat-grid" style={{ marginTop: 14 }}>
                <div className="opt-stat">
                  <span>Paper cash</span>
                  <strong>{fmt(portfolio.cash ?? 0)}</strong>
                </div>
                <div className="opt-stat">
                  <span>Spot (bot)</span>
                  <strong>{fmt(autoStatus?.lastPrice ?? 0)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="opt-card">
            <h3>Live activity</h3>
            <div className="opt-log">
              {(autoStatus?.activityLog || []).slice(0, 12).map((entry, i) => (
                <div key={i} className="opt-log-line">
                  <span className="opt-log-time">{formatActivityWhen(entry)}</span>
                  {entry.message}
                </div>
              ))}
              {(!autoStatus?.activityLog || autoStatus.activityLog.length === 0) && (
                <div className="opt-position-empty">Waiting for market activity…</div>
              )}
            </div>
          </div>

          <HindiNotifyPanel
            activityLog={autoStatus?.activityLog}
            lastAction={autoStatus?.lastAction}
            lastHindiAlert={autoStatus?.lastHindiAlert}
          />

          <details className="opt-details">
            <summary>Advanced analytics</summary>
            <div className="opt-details-body">
              <PaceAnalytics analytics={autoStatus?.analytics} sessionPhase={autoStatus?.sessionPhase} />
              <QuantDashboard
                mode="options"
                analytics={autoStatus?.analytics}
                sessionPhase={autoStatus?.sessionPhase}
                optionsQuant={autoStatus?.quant}
                niftyChange={prices.find((p) => p.symbol === '^NSEI')?.change ?? 0}
              />
            </div>
          </details>
        </>
      )}

      {activeTab === 'settings' && (
        <div className="opt-card">
          <h3>Automation settings <span className="opt-card-hint">— saves automatically</span></h3>
          {settingsSaved && <span className="opt-saved">Saved</span>}

          <div className="opt-settings-section">
            <h4>Market</h4>
            <div className="opt-field-row">
              <div className="opt-field">
                <label htmlFor="opt-underlying">Underlying</label>
                <select
                  id="opt-underlying"
                  value={settings.symbol}
                  onChange={(e) => {
                    const map = { '^NSEI': 'NIFTY', 'RELIANCE.NS': 'RELIANCE', 'TCS.NS': 'TCS', 'HDFCBANK.NS': 'HDFCBANK', 'INFY.NS': 'INFY' };
                    saveSettings({ ...settings, symbol: e.target.value, underlyingName: map[e.target.value] || 'NIFTY' });
                  }}
                >
                  <option value="^NSEI">NIFTY</option>
                  <option value="RELIANCE.NS">RELIANCE</option>
                  <option value="TCS.NS">TCS</option>
                  <option value="HDFCBANK.NS">HDFCBANK</option>
                  <option value="INFY.NS">INFY</option>
                </select>
              </div>
              <div className="opt-field">
                <label htmlFor="opt-type">Option type</label>
                <select
                  id="opt-type"
                  value={settings.optionType}
                  onChange={(e) => updateSetting('optionType', e.target.value)}
                >
                  <option value="auto">Auto (CE if up, PE if down)</option>
                  <option value="CE">Call (CE) only</option>
                  <option value="PE">Put (PE) only</option>
                </select>
              </div>
              <div className="opt-field">
                <label htmlFor="opt-trigger">Entry trigger %</label>
                <input
                  id="opt-trigger"
                  type="number"
                  step="0.05"
                  value={settings.triggerPct}
                  onChange={(e) => updateSetting('triggerPct', parseFloat(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="opt-settings-section">
            <h4>Targets &amp; risk</h4>
            <div className="opt-field-row">
              <div className="opt-field">
                <label htmlFor="opt-target">Daily profit target ₹</label>
                <input
                  id="opt-target"
                  type="number"
                  value={settings.dailyTarget}
                  onChange={(e) => updateSetting('dailyTarget', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-invest">Max invest / day ₹</label>
                <input
                  id="opt-invest"
                  type="number"
                  value={settings.maxDailyInvestment}
                  onChange={(e) => updateSetting('maxDailyInvestment', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-stop">Stop loss / trade ₹</label>
                <input
                  id="opt-stop"
                  type="number"
                  value={settings.stopLossPerTrade}
                  onChange={(e) => updateSetting('stopLossPerTrade', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-maxloss">Max loss / day ₹</label>
                <input
                  id="opt-maxloss"
                  type="number"
                  value={settings.maxDailyLoss}
                  onChange={(e) => updateSetting('maxDailyLoss', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-lots">Lots</label>
                <input
                  id="opt-lots"
                  type="number"
                  value={settings.lots}
                  onChange={(e) => updateSetting('lots', parseInt(e.target.value, 10))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-lotsize">Lot size</label>
                <input
                  id="opt-lotsize"
                  type="number"
                  value={settings.lotSize}
                  onChange={(e) => updateSetting('lotSize', parseInt(e.target.value, 10))}
                />
              </div>
            </div>
          </div>

          <div className="opt-settings-section">
            <h4>Schedule</h4>
            <div className="opt-checks">
              <label>
                <input
                  type="checkbox"
                  checked={settings.autoStartAtMarketOpen}
                  onChange={(e) => updateSetting('autoStartAtMarketOpen', e.target.checked)}
                />
                Auto-start at 9:15 AM IST
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.autoOpenBrowser}
                  onChange={(e) => updateSetting('autoOpenBrowser', e.target.checked)}
                />
                Open this screen at market open
              </label>
            </div>
          </div>

          <p className="opt-settings-note">
            <strong>Hands-free locked ON.</strong> Bot invests up to <strong>{fmt(settings.maxDailyInvestment)}</strong>/day,
            uses India ML for CE/PE, keeps going until <strong>{fmt(settings.dailyTarget)}</strong>,
            and auto square-off at 3:15 IST. Poll every {settings.pollSeconds}s. You do not need to open this page for it to trade.
          </p>
        </div>
      )}

      {activeTab === 'history' && (
        <>
          <div className="opt-card">
            <h3>Today — {today.date ? formatIstDateKey(today.date) : '—'}</h3>
            <div className="opt-stat-grid">
              <div className="opt-stat">
                <span>Invested</span>
                <strong>{fmt(today.totalInvested ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Returned</span>
                <strong>{fmt(today.totalReturned ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Net P&amp;L</span>
                <strong className={(today.netPnl ?? 0) >= 0 ? 'up' : 'down'}>{fmt(today.netPnl ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Trades</span>
                <strong>{today.tradesCount ?? 0} ({today.winCount ?? 0}W / {today.lossCount ?? 0}L)</strong>
              </div>
            </div>
            <div className="opt-lifetime">
              <strong>Lifetime:</strong> invested {fmt(summary.totalInvested ?? 0)} · net {fmt(summary.netPnl ?? 0)} ·
              target hit {summary.targetHitDays ?? 0} days · win/loss days {summary.winDays ?? 0}/{summary.lossDays ?? 0}
            </div>
          </div>

          <div className="opt-card">
            <h3>Past days</h3>
            <div className="opt-table-wrap">
              <table className="opt-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invested</th>
                    <th>Returned</th>
                    <th>Net P&amp;L</th>
                    <th>Trades</th>
                    <th>Target</th>
                  </tr>
                </thead>
                <tbody>
                  {(dailyHistory?.history || []).map((day, i) => (
                    <tr key={i} className={day.netPnl >= day.target ? 'row-win' : day.netPnl < 0 ? 'row-loss' : ''}>
                      <td>{day.dateTime || formatIstDateKey(day.date)}</td>
                      <td>{fmt(day.totalInvested)}</td>
                      <td>{fmt(day.totalReturned)}</td>
                      <td>{fmt(day.netPnl)}</td>
                      <td>{day.tradesCount}</td>
                      <td>{day.targetHit ? 'Yes' : '—'}</td>
                    </tr>
                  ))}
                  {(!dailyHistory?.history || dailyHistory.history.length === 0) && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: 'var(--opt-muted)' }}>
                        No past days yet — history appears after trading sessions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="opt-card">
            <h3>Trade log</h3>
            <div className="opt-log">
              {trades.length > 0 ? trades.slice(-20).reverse().map((t, i) => (
                <div key={i} className="opt-log-line">
                  <span className="opt-log-time">{t.time}</span>
                  {t.type} {t.symbol} ×{t.qty} @ {fmt(t.price)}
                  {t.pnl !== 0 && ` · P&L ${fmt(t.pnl)}`}
                </div>
              )) : (
                <div className="opt-position-empty">No trades yet — bot trades automatically when market is open</div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'stress' && (
        <div className="opt-card">
          <h3>Stress test — 1,00,000 dummy Options records</h3>
          <p style={{ color: 'var(--opt-muted)', fontSize: '0.85rem', marginTop: 0 }}>
            Separate from live paper portfolio. Use this to prove pagination + India ML bias before tomorrow 9:15 IST.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <button type="button" className="opt-btn primary" disabled={stressBusy} onClick={() => runStressGenerate(false)}>
              {stressBusy ? 'Working…' : 'Generate 1 lakh JSON'}
            </button>
            <button type="button" className="opt-btn" disabled={stressBusy} onClick={() => runStressGenerate(true)}>
              Force regenerate
            </button>
            <button type="button" className="opt-btn" disabled={stressBusy} onClick={runReadiness}>
              Run readiness test
            </button>
          </div>

          {stressToast && (
            <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: 'rgba(59,130,246,0.12)', fontSize: '0.82rem' }}>
              {stressToast}
            </div>
          )}

          <div className="opt-stat-grid">
            <div className="opt-stat">
              <span>Records</span>
              <strong>{(stressMeta?.count || 0).toLocaleString('en-IN')}</strong>
            </div>
            <div className="opt-stat">
              <span>Dummy net P&amp;L</span>
              <strong className={(stressMeta?.netPnl || 0) >= 0 ? 'up' : 'down'}>{fmt(stressMeta?.netPnl || 0)}</strong>
            </div>
            <div className="opt-stat">
              <span>Win rate</span>
              <strong>{stressMeta?.winRate != null ? `${stressMeta.winRate}%` : '—'}</strong>
            </div>
            <div className="opt-stat">
              <span>File size</span>
              <strong>{stressMeta?.sizeMb != null ? `${stressMeta.sizeMb} MB` : '—'}</strong>
            </div>
          </div>

          {stressReady && (
            <div style={{
              margin: '12px 0',
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${stressReady.ok ? '#22c55e' : '#f87171'}`,
              background: stressReady.ok ? 'rgba(34,197,94,0.08)' : 'rgba(248,113,113,0.08)'
            }}>
              <strong>{stressReady.readyForTomorrow ? 'READY FOR TOMORROW' : 'NOT READY'}</strong>
              <div style={{ fontSize: '0.8rem', marginTop: 4 }}>{stressReady.message}</div>
              {stressReady.indiaMlBias && (
                <div style={{ fontSize: '0.78rem', marginTop: 6 }}>
                  India ML bias: <strong>{stressReady.indiaMlBias.optionBias}</strong>
                  {stressReady.indiaMlBias.reason ? ` — ${stressReady.indiaMlBias.reason}` : ''}
                </div>
              )}
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: '0.75rem' }}>
                {(stressReady.checks || []).map((c) => (
                  <li key={c.name} style={{ color: c.pass ? '#4ade80' : '#f87171' }}>
                    {c.pass ? 'PASS' : 'FAIL'} · {c.name} · {c.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button
              type="button"
              className="opt-btn"
              disabled={stressBusy || stressPageNo <= 1}
              onClick={() => loadStressPage(stressPageNo - 1)}
            >
              Prev
            </button>
            <span style={{ fontSize: '0.8rem' }}>
              Page {stressPage?.page || stressPageNo} / {stressPage?.pages || '—'}
            </span>
            <button
              type="button"
              className="opt-btn"
              disabled={stressBusy || !stressPage || stressPageNo >= (stressPage.pages || 1)}
              onClick={() => loadStressPage(stressPageNo + 1)}
            >
              Next
            </button>
          </div>

          <div className="opt-table-wrap">
            <table className="opt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Contract</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {(stressPage?.items || []).map((t) => (
                  <tr key={t.id}>
                    <td>{t.i}</td>
                    <td>{t.date} {t.time}</td>
                    <td>{t.type}</td>
                    <td>{t.symbol}</td>
                    <td>{t.qty}</td>
                    <td>{fmt(t.price)}</td>
                    <td className={t.pnl > 0 ? 'up' : t.pnl < 0 ? 'down' : ''}>{fmt(t.pnl)}</td>
                  </tr>
                ))}
                {(!stressPage?.items || stressPage.items.length === 0) && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--opt-muted)' }}>
                      Click &quot;Generate 1 lakh JSON&quot; to create dummy data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="opt-footer">
        🤖 FULLY AUTO — no Start button needed. Server starts CE/PE trades alone at 9:15 IST, takes profit toward ₹{settings.dailyTarget || 100}, squares off by 3:15. Paper only.
      </p>
    </div>
  );
};

export default OptionsTrading;
